import React from 'react'
import type { GoLiveGateVO, GoLiveProgramVO } from '../../../apis/types'
import { ownerLabel } from './gateStyles'

// The F2 "why no submission" treatment (WEB-6439): the dependent allocation
// chain — TESA coverage -> build & file -> active at utility — with PTO shown as
// the clock, and the OneBill account gauge beside the flow (parallel billing-
// readiness, not a step). See wireframes/why-not-submitted-mockups-v2.html.

const gatePrefix = (gateId: string): string => gateId.split('_').slice(0, 3).join('_')

const findGate = (program: GoLiveProgramVO, prefix: string): GoLiveGateVO | undefined =>
    program.gates.find((g) => gatePrefix(g.gate) === prefix)

type NodeState = 'done' | 'blocked' | 'waiting' | 'future'

function nodeState(gate: GoLiveGateVO | undefined): NodeState {
    if (gate == null || gate.verdict === 'unknown') return 'future'
    if (gate.verdict === 'pass') return 'done'
    if (gate.verdict === 'waiting-external') return 'waiting'
    return 'blocked' // fail-ours
}

// Tailwind classes per node state. Blocked nodes get a ring to read as the stall.
const NODE_STYLES: Record<NodeState, string> = {
    done: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20',
    blocked: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-400 dark:ring-red-500',
    waiting: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 ring-2 ring-amber-400 dark:ring-amber-500',
    future: 'border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40',
}

const MARK: Record<NodeState, string> = { done: '●', blocked: '✕', waiting: '◆', future: '○' }

const StepNode: React.FC<{
    title: string
    detail: string
    owner: string | null
    state: NodeState
}> = ({ title, detail, owner, state }) => (
    <div className={`flex-1 min-w-0 rounded-lg border px-2.5 py-2 ${NODE_STYLES[state]}`}>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
            <span aria-hidden="true">{MARK[state]}</span>
            <span className="truncate">{title}</span>
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{detail}</div>
        {owner != null && (
            <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {ownerLabel(owner)}
            </span>
        )}
    </div>
)

const Arrow: React.FC = () => (
    <span className="flex-none text-slate-300 dark:text-slate-600 text-sm" aria-hidden="true">
        →
    </span>
)

// The pass/fail threshold is owned by Salesforce (TESA_Threshold_Met__c), so we
// show the measured percent but not a hardcoded threshold number that could drift.
function tesaDetail(gate: GoLiveGateVO | undefined, pct: number | null): string {
    if (gate == null || gate.verdict === 'unknown') return 'no Salesforce data'
    if (gate.verdict === 'pass') return pct != null ? `${Math.round(pct)}% of output — met` : 'threshold met'
    return pct != null ? `${Math.round(pct)}% of output — below threshold` : 'below threshold'
}

// Post-submission gates that carry real KE work once a table is filed. G_A_05
// is omitted: it is program_meter hygiene, not a go-live blocker (see backend
// HYGIENE_GATE_PREFIXES). G_A_02 (active at utility) already has its own node in
// the flow, so it is not repeated here.
const POST_SUBMISSION_PREFIXES = ['G_A_03', 'G_A_04', 'G_A_06', 'G_A_07']

export const SubmissionFlow: React.FC<{ program: GoLiveProgramVO }> = ({ program }) => {
    const tesa = findGate(program, 'G_A_00')
    const filed = findGate(program, 'G_A_01')
    const active = findGate(program, 'G_A_02')

    // Once a submission is filed (G_A_01 pass), surface the failing post-
    // submission gates the compact row hides behind the single binding owner
    // (WEB-6439). Read straight from the VO's per-gate presentation fields.
    const submissionFiled = filed?.verdict === 'pass'
    const alsoNeedsFixing = submissionFiled
        ? POST_SUBMISSION_PREFIXES.map((prefix) => findGate(program, prefix)).filter(
              (g): g is GoLiveGateVO => g != null && g.verdict === 'fail-ours',
          )
        : []

    const tesaState = nodeState(tesa)
    // "Build & file" is the submission-exists gate; once filed it is done.
    const filedState = nodeState(filed)
    const activeState = nodeState(active)

    // Overdue: past PTO and not yet billing (the worklist buckets). Filing
    // normally precedes PTO, so a worklist program with no submission is late.
    const overdue = program.bucket === 'recently-live' || program.bucket === 'behind'

    const ob = program.oneBillAccounts
    const obPct = ob != null && ob.expected > 0 ? Math.round((ob.present / ob.expected) * 100) : 0
    const obComplete = ob != null && ob.present >= ob.expected

    return (
        <div className="flex flex-col lg:flex-row gap-4 px-6 py-4 bg-slate-50/60 dark:bg-slate-800/30">
            <div className="flex-1 min-w-0">
                {/* Generation track is context: for worklist programs PTO has passed. */}
                <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
                    Generation:{' '}
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                        ● PTO {program.ptoDate ? program.ptoDate.slice(0, 10) : '—'}
                    </span>
                    {overdue && (
                        <span className="ml-2 text-red-600 dark:text-red-400 font-semibold normal-case">
                            · filing overdue
                        </span>
                    )}
                </div>
                {/* Allocation track: the dependent chain to a utility-active submission. */}
                <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
                    Allocation → utility
                </div>
                <div className="flex items-stretch gap-2">
                    <StepNode
                        title="TESA coverage"
                        detail={tesaDetail(tesa, program.tesaPercentOutput)}
                        owner={tesa?.owner ?? null}
                        state={tesaState}
                    />
                    <Arrow />
                    <StepNode
                        title="Build & file"
                        detail={filedState === 'done' ? 'submission filed' : 'no table filed yet'}
                        owner={filed?.owner ?? null}
                        state={filedState}
                    />
                    <Arrow />
                    <StepNode
                        title="Active at utility"
                        detail={
                            activeState === 'done'
                                ? 'active'
                                : activeState === 'waiting'
                                  ? 'awaiting utility'
                                  : 'needs a filed table'
                        }
                        owner={active?.owner ?? 'utility'}
                        state={activeState}
                    />
                </div>

                {/* Parallel KE work the binding step hides: failing post-
                    submission gates a filed-but-not-active program still owns. */}
                {alsoNeedsFixing.length > 0 && (
                    <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-wide text-red-500 dark:text-red-400 mb-1 font-semibold">
                            Also needs fixing
                        </div>
                        <ul className="space-y-1">
                            {alsoNeedsFixing.map((gate) => (
                                <li
                                    key={gate.gate}
                                    className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300"
                                >
                                    <span className="text-red-500 dark:text-red-400" aria-hidden="true">
                                        ✕
                                    </span>
                                    <span className="font-medium">{gate.label}</span>
                                    {gate.failureClass != null && (
                                        <span className="text-slate-400 dark:text-slate-500">— {gate.failureClass}</span>
                                    )}
                                    {gate.owner != null && (
                                        <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            {ownerLabel(gate.owner)}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Parallel billing-readiness gauge — NOT a step in the flow. */}
            <div className="lg:w-44 flex-none rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/60 px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[9px] uppercase tracking-wide text-slate-400 dark:text-slate-500 font-semibold">
                        OneBill accounts
                    </span>
                    <span className="text-[8px] uppercase tracking-wide font-bold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded">
                        parallel
                    </span>
                </div>
                {ob != null ? (
                    <>
                        <div className="text-lg font-semibold tabular-nums text-slate-700 dark:text-slate-200 mt-1 leading-none">
                            {ob.present}
                            <span className="text-xs text-slate-400 dark:text-slate-500"> / {ob.expected} set up</span>
                        </div>
                        <div className="h-1.5 rounded bg-slate-200 dark:bg-slate-700 mt-2 overflow-hidden">
                            <div
                                className={`h-full rounded ${obComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                                style={{ width: `${Math.max(obPct, 3)}%` }}
                            />
                        </div>
                        <div className="text-[9px] text-slate-400 dark:text-slate-500 mt-1.5">
                            Billing-readiness, not a filing blocker.
                        </div>
                    </>
                ) : (
                    <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5">
                        No expected accounts (no Salesforce TESA data).
                    </div>
                )}
            </div>
        </div>
    )
}
